import LazyLoadedImage from 'Components/shared/LazyLoadedImage';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	getAlbums,
	neighborPhotoKey,
	photoSrc,
	themeVariant,
} from 'modules/gallery';
import Theme from 'contexts/Theme';

const KEY_DIRECTIONS = { ArrowLeft: -1, ArrowRight: 1 };

const isTyping = (target) =>
	target?.isContentEditable ||
	['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName);

export default () => {
	const [metadata, setMetadata] = useState({});
	const [loaded, setLoaded] = useState(false);
	const theme = useContext(Theme);
	const navigate = useNavigate();
	const { collectionId, photoId } = useParams();

	const fetchMetadata = async () => {
		const metadata = await getAlbums();

		setMetadata(metadata);
		setLoaded(true);
	};

	useEffect(() => {
		fetchMetadata();
	}, []);

	const collectionMetadata = metadata[collectionId];
	const photo = collectionMetadata?.photos?.[photoId];

	// Left/right arrows step through the collection. Replacing the history
	// entry keeps Back going straight to the collection.
	useEffect(() => {
		if (!photo) return;

		const neighbors = Object.fromEntries(
			Object.entries(KEY_DIRECTIONS).map(([key, direction]) => [
				key,
				neighborPhotoKey(collectionMetadata, photoId, direction),
			])
		);

		// Warm the cache so the next photo appears immediately.
		Object.values(neighbors)
			.filter(Boolean)
			.forEach((key) => {
				new Image().src = photoSrc(
					collectionId,
					themeVariant(theme),
					key,
					collectionMetadata.photos[key]
				);
			});

		const onKeyDown = (e) => {
			const target = neighbors[e.key];

			if (
				!target ||
				e.defaultPrevented ||
				e.altKey ||
				e.ctrlKey ||
				e.metaKey ||
				e.shiftKey ||
				isTyping(e.target)
			)
				return;

			e.preventDefault();
			navigate(`/photo-view/${collectionId}/${target}`, { replace: true });
		};

		window.addEventListener('keydown', onKeyDown);

		return () => window.removeEventListener('keydown', onKeyDown);
	}, [collectionId, photoId, photo, collectionMetadata, theme, navigate]);

	if (!loaded) {
		return;
	}

	if (!photo) {
		return (
			<div className='h-full w-full p-5 flex flex-col place-items-center'>
				<span className='text-xl'>Photo not found</span>
			</div>
		);
	}

	const path = `${collectionId}/${photoId}`;

	return (
		<div className='h-full w-full p-5 flex flex-col gap-5 place-items-center'>
			<span className='text-xl'>{collectionMetadata.description}</span>
			<LazyLoadedImage
				key={path}
				src={photoSrc(collectionId, themeVariant(theme), photoId, photo)}
				className='w-full max-h-[100vh] pb-5 object-contain'
				alt={path}
				placeholderSrc={photoSrc(collectionId, 'lowres', photoId, photo)}
			/>
		</div>
	);
};
